import { __rest } from "tslib";
import * as React from "react";
import * as SheetPrimitive from "@radix-ui/react-dialog";
import { cva } from "class-variance-authority";
import { X } from "lucide-react";
import { cn } from "../../lib/utils";
/**
 * @example
 *
 * ```jsx
 * import {
 *   Button,
 *   Input,
 *   Label,
 *   Sheet,
 *   SheetClose,
 *   SheetContent,
 *   SheetDescription,
 *   SheetFooter,
 *   SheetHeader,
 *   SheetTitle,
 *   SheetTrigger,
 * } from "@skve/components"
 *
 * function Demo() {
 *   return (
 *     <Sheet>
 *       <SheetTrigger asChild>
 *         <Button variant="outline">Open</Button>
 *       </SheetTrigger>
 *       <SheetContent>
 *         <SheetHeader>
 *           <SheetTitle>Edit profile</SheetTitle>
 *           <SheetDescription>
 *             Make changes to your profile here. Click save when you're done.
 *           </SheetDescription>
 *         </SheetHeader>
 *         <div className="grid gap-4 py-4">
 *           <div className="grid grid-cols-4 items-center gap-4">
 *             <Label htmlFor="name" className="text-right">
 *               Name
 *             </Label>
 *             <Input id="name" value="Pedro Duarte" className="col-span-3" />
 *           </div>
 *           <div className="grid grid-cols-4 items-center gap-4">
 *             <Label htmlFor="username" className="text-right">
 *               Username
 *             </Label>
 *             <Input id="username" value="@peduarte" className="col-span-3" />
 *           </div>
 *         </div>
 *         <SheetFooter>
 *           <SheetClose asChild>
 *             <Button type="submit">Save changes</Button>
 *           </SheetClose>
 *         </SheetFooter>
 *       </SheetContent>
 *     </Sheet>
 *   )
 * }
 * ```
 */
const Sheet = SheetPrimitive.Root;
const SheetTrigger = SheetPrimitive.Trigger;
const SheetClose = SheetPrimitive.Close;
const SheetPortal = SheetPrimitive.Portal;
const SheetOverlay = React.forwardRef((_a, ref) => {
    var { className } = _a, props = __rest(_a, ["className"]);
    return (React.createElement(SheetPrimitive.Overlay, Object.assign({ className: cn("fixed inset-0 z-[500] bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0", className) }, props, { ref: ref })));
});
SheetOverlay.displayName = SheetPrimitive.Overlay.displayName;
const sheetVariants = cva("fixed z-[500] gap-4 bg-background p-6 shadow-lg transition ease-in-out data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:duration-300 data-[state=open]:duration-500 app-sheet", {
    variants: {
        side: {
            top: "inset-x-0 top-0 border-b data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top",
            bottom: "inset-x-0 bottom-0 border-t data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom",
            left: "inset-y-0 left-0 h-full w-full sm:w-3/4 border-r data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left sm:max-w-sm",
            right: "inset-y-0 right-0 h-full w-full sm:w-3/4  border-l data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right sm:max-w-sm",
        },
    },
    defaultVariants: {
        side: "right",
    },
});
const SheetContent = React.forwardRef((_a, ref) => {
    var { side = "right", className, children } = _a, props = __rest(_a, ["side", "className", "children"]);
    return (React.createElement(SheetPortal, null,
        React.createElement(SheetOverlay, null),
        React.createElement(SheetPrimitive.Content, Object.assign({ ref: ref, className: cn(sheetVariants({ side }), className), "data-side": side }, props),
            children,
            React.createElement(SheetPrimitive.Close, { className: "absolute right-3 top-3 rounded-full opacity-70 transition-opacity hover:opacity-100 disabled:pointer-events-none bg-primary p-1 text-white" },
                React.createElement(X, { className: "h-3 w-3" }),
                React.createElement("span", { className: "sr-only" }, "Schlie\u00DFen")))));
});
SheetContent.displayName = SheetPrimitive.Content.displayName;
const SheetHeader = (_a) => {
    var { className } = _a, props = __rest(_a, ["className"]);
    return (React.createElement("div", Object.assign({ className: cn("flex flex-col space-y-2 text-center sm:text-left", className) }, props)));
};
SheetHeader.displayName = "SheetHeader";
const SheetFooter = (_a) => {
    var { className } = _a, props = __rest(_a, ["className"]);
    return (React.createElement("div", Object.assign({ className: cn("flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2", className) }, props)));
};
SheetFooter.displayName = "SheetFooter";
const SheetTitle = React.forwardRef((_a, ref) => {
    var { className } = _a, props = __rest(_a, ["className"]);
    return (React.createElement(SheetPrimitive.Title, Object.assign({ ref: ref, className: cn("text-lg font-semibold text-foreground", className) }, props)));
});
SheetTitle.displayName = SheetPrimitive.Title.displayName;
const SheetDescription = React.forwardRef((_a, ref) => {
    var { className } = _a, props = __rest(_a, ["className"]);
    return (React.createElement(SheetPrimitive.Description, Object.assign({ ref: ref, className: cn("text-sm text-muted-foreground", className) }, props)));
});
SheetDescription.displayName = SheetPrimitive.Description.displayName;
export { Sheet, SheetPortal, SheetOverlay, SheetTrigger, SheetClose, SheetContent, SheetHeader, SheetFooter, SheetTitle, SheetDescription, };
//# sourceMappingURL=sheet.js.map